import { useEffect, useRef, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useAssetsStore } from '@/stores/assetsStore';
import { useLinkStore } from '@/stores/linkStore';
import { useRisks, useIncidents, useControls, useTasks } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { calculateSiteMetrics, getRiskLevelColor, SiteMetrics } from '@/utils/siteMetrics';
import { Endpoint } from '@/types/endpoint';
import { useNavigate } from 'react-router-dom';

export default function AssetsMapView() {
  const navigate = useNavigate();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const boundsSetRef = useRef(false);
  const markersCreatedForSitesRef = useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  
  const { sites, loadSites } = useSiteStore();
  const { zones, loadZones } = useZoneStore();
  const { systems, loadSystems } = useSystemStore();
  const { nodes, loadAssets } = useAssetsStore();
  const { links, loadLinks } = useLinkStore();
  const { organization } = useAuth();
  
  const { risks } = useRisks(organization?.id);
  const { incidents } = useIncidents(organization?.id);
  const { controls } = useControls(organization?.id);
  const { tasks } = useTasks(organization?.id);
  
  // Load all data on mount
  useEffect(() => {
    loadSites();
    loadZones();
    loadSystems();
    loadAssets();
    loadLinks();
  }, [loadSites, loadZones, loadSystems, loadAssets, loadLinks]);
  
  // Memoize endpoints conversion
  const endpoints = useMemo<Endpoint[]>(() => {
    return nodes.map(node => ({
      id: node.id,
      org_id: organization?.id || '',
      name: node.data.label,
      type: node.data.type as string,
      system_id: (node.data.system_id as string | undefined) || undefined,
      site_id: (node.data.site_id as string | undefined) || undefined,
      created_at: '',
      updated_at: '',
    }));
  }, [nodes, organization?.id]);
  
  // Memoize site metrics calculation
  const siteMetrics = useMemo<SiteMetrics[]>(() => {
    if (!risks || !incidents || !controls || !tasks) return [];
    
    return sites
      .filter(site => site.metadata?.coordinates?.lat && site.metadata?.coordinates?.lon)
      .map(site => 
        calculateSiteMetrics(
          site,
          zones,
          systems,
          endpoints,
          links,
          risks,
          controls,
          incidents,
          tasks
        )
      );
  }, [sites, zones, systems, endpoints, links, risks, incidents, controls, tasks]);

  // Initialize map once
  useEffect(() => {
    if (!mapContainer.current) return;

    const initializeMap = async () => {
      try {
        setLoading(true);
        
        // Fetch Mapbox token from edge function
        const { data, error: tokenError } = await supabase.functions.invoke('get-mapbox-token');
        
        if (tokenError) throw tokenError;
        if (!data?.token) throw new Error('No Mapbox token available');

        mapboxgl.accessToken = data.token;

        // Double check container still exists
        if (!mapContainer.current) {
          throw new Error('Map container not available');
        }

        // Initialize map
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/light-v11',
          center: [15, 55], // Centered on Europe/Scandinavia
          zoom: 4,
        });

        // Add navigation controls
        map.current.addControl(
          new mapboxgl.NavigationControl(),
          'top-right'
        );

        map.current.on('load', () => {
          setLoading(false);
          setMapReady(true);
        });

      } catch (err) {
        console.error('Map initialization error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load map');
        setLoading(false);
      }
    };

    initializeMap();

    return () => {
      // Clean up markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      markersCreatedForSitesRef.current.clear();
      
      // Only remove if map was successfully created
      if (map.current && map.current.getContainer()) {
        map.current.remove();
        map.current = null;
      }
      setMapReady(false);
    };
  }, []); // Only initialize once

  // Create markers only once when map is ready and we have metrics
  useEffect(() => {
    if (!map.current || !mapReady || loading || siteMetrics.length === 0) return;
    
    // Sites with coordinates
    const sitesWithCoords = sites.filter(site => 
      site.metadata?.coordinates?.lat && site.metadata?.coordinates?.lon
    );
    
    // Check if we need to create any new markers
    const sitesNeedingMarkers = sitesWithCoords.filter(
      site => !markersCreatedForSitesRef.current.has(site.id)
    );
    
    // If all sites already have markers, do nothing
    if (sitesNeedingMarkers.length === 0) return;
    
    console.log(`AssetsMapView: Creating markers for ${sitesNeedingMarkers.length} new sites`);
    
    // Add new markers - ONLY for sites that don't have them yet
    sitesNeedingMarkers.forEach(site => {
      if (!map.current) return;
      
      const coords = site.metadata!.coordinates!;
      const metrics = siteMetrics.find(m => m.siteId === site.id);
      if (!metrics) return;
      
      const riskColor = getRiskLevelColor(metrics.overallRiskLevel);
      const criticalIssues = metrics.riskCount + metrics.incidentCount;

      // Create site marker with risk-based coloring
      const el = document.createElement('div');
      el.className = 'site-marker';
      el.style.backgroundColor = riskColor;
      el.style.width = '36px';
      el.style.height = '36px';
      el.style.borderRadius = '50%';
      el.style.border = '3px solid white';
      el.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
      el.style.cursor = 'pointer';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.position = 'relative';
      el.innerHTML = '🏢';
      el.style.fontSize = '18px';

      // Add badge for critical issues
      if (criticalIssues > 0) {
        const badge = document.createElement('div');
        badge.style.cssText = `
          position: absolute;
          top: -6px;
          right: -6px;
          background: #dc2626;
          color: white;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        `;
        badge.textContent = criticalIssues.toString();
        el.appendChild(badge);
      }

      // Create enhanced popup with all metrics
      const highRisks = Math.floor(metrics.riskCount * 0.3);
      
      const popup = new mapboxgl.Popup({ offset: 25, maxWidth: '300px' }).setHTML(`
        <div style="padding: 12px; font-family: system-ui, -apple-system, sans-serif;">
          <h3 style="font-weight: 700; margin-bottom: 8px; font-size: 15px; color: #1a1a1a;">
            📍 ${site.name}
          </h3>
          ${site.site_code ? `<p style="font-size: 12px; color: #666; margin-bottom: 8px;"><strong>Code:</strong> ${site.site_code}</p>` : ''}
          
          <div style="border-top: 1px solid #e5e7eb; padding-top: 8px; margin-top: 8px;">
            <div style="font-size: 11px; font-weight: 600; color: #374151; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">
              🏢 Infrastructure
            </div>
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; font-size: 12px; color: #4b5563; margin-bottom: 8px;">
              <div><strong>${metrics.zoneCount}</strong> Zones</div>
              <div><strong>${metrics.systemCount}</strong> Systems</div>
              <div><strong>${metrics.endpointCount}</strong> Endpoints</div>
            </div>
          </div>
          
          <div style="border-top: 1px solid #e5e7eb; padding-top: 8px; margin-top: 8px;">
            <div style="font-size: 11px; font-weight: 600; color: #374151; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">
              ⚠️ Compliance & Risk
            </div>
            <div style="font-size: 12px; color: #4b5563; line-height: 1.6;">
              ${metrics.riskCount > 0 
                ? `<div style="color: ${metrics.riskCount > 5 ? '#dc2626' : '#f59e0b'};">• <strong>${metrics.riskCount}</strong> Open Risk${metrics.riskCount !== 1 ? 's' : ''} ${highRisks > 0 ? `(${highRisks} High)` : ''}</div>` 
                : '<div style="color: #10b981;">• No open risks</div>'}
              ${metrics.incidentCount > 0 
                ? `<div style="color: #dc2626;">• <strong>${metrics.incidentCount}</strong> Open Incident${metrics.incidentCount !== 1 ? 's' : ''}</div>` 
                : '<div style="color: #10b981;">• No open incidents</div>'}
              ${metrics.controlCount > 0 
                ? `<div>• <strong>${metrics.controlCount}</strong> Control Gap${metrics.controlCount !== 1 ? 's' : ''}</div>` 
                : '<div style="color: #10b981;">• All controls implemented</div>'}
              ${metrics.taskCount > 0 
                ? `<div>• <strong>${metrics.taskCount}</strong> Pending Task${metrics.taskCount !== 1 ? 's' : ''}</div>` 
                : '<div style="color: #10b981;">• No pending tasks</div>'}
            </div>
          </div>
        </div>
      `);

      // Add site marker to map with FIXED coordinates
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center'
      })
        .setLngLat([coords.lon, coords.lat])
        .setPopup(popup)
        .addTo(map.current);

      markersRef.current.push(marker);
      markersCreatedForSitesRef.current.add(site.id);
    });

    // Fit bounds to show all markers (only once)
    if (sitesNeedingMarkers.length > 0 && !boundsSetRef.current) {
      const bounds = new mapboxgl.LngLatBounds();
      sitesWithCoords.forEach(site => {
        if (site.metadata?.coordinates) {
          bounds.extend([site.metadata.coordinates.lon, site.metadata.coordinates.lat]);
        }
      });
      
      if (map.current) {
        map.current.fitBounds(bounds, { padding: 50, maxZoom: 12 });
        boundsSetRef.current = true;
      }
    }
  }, [mapReady, loading, siteMetrics, sites]); // Only depend on these stable values

  // Empty state - no sites
  if (sites.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 bg-background/80 backdrop-blur-sm">
        <MapPin className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No sites to display on map</h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-md">
          Create your first site with location coordinates to see it on the map
        </p>
        <Button onClick={() => navigate('/assets?view=browser')}>
          <Plus className="h-4 w-4 mr-2" />
          Create First Site
        </Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="text-danger text-lg font-semibold">Map Error</div>
              <p className="text-sm text-muted-foreground">{error}</p>
              <p className="text-xs text-muted-foreground">
                Make sure the MAPBOX_PUBLIC_TOKEN is configured in Supabase Edge Function Secrets.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
          <div className="flex items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-sm text-muted-foreground">Loading map...</span>
          </div>
        </div>
      )}
      <div ref={mapContainer} className="absolute inset-0 rounded-lg" />
      
      {/* Legend */}
      {!loading && (
        <div className="absolute bottom-4 left-4 bg-background/95 border rounded-lg p-3 shadow-lg">
          <div className="text-xs font-semibold mb-3">Legend</div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs mb-2">
              <div className="w-4 h-4 rounded-full flex items-center justify-center text-xs">🏢</div>
              <span className="font-medium">Site Markers</span>
            </div>
            <div className="border-t pt-2 space-y-1">
              <div className="text-xs font-medium mb-1">Risk Level:</div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span>Low</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span>Medium</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <span>High</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span>Critical</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Site count */}
      {!loading && (
        <div className="absolute top-4 left-4 bg-background/95 border rounded-lg px-3 py-2 shadow-lg">
          <div className="text-xs text-muted-foreground">
            Showing <strong>{siteMetrics.length}</strong> sites with{' '}
            <strong>{siteMetrics.reduce((sum, m) => sum + m.zoneCount + m.systemCount + m.endpointCount, 0)}</strong> total assets
          </div>
        </div>
      )}
    </div>
  );
}
